<script>
    import Cookies from 'js-cookie';
    import Modal from 'sveltestrap/src/Modal.svelte';
    import ModalBody from 'sveltestrap/src/ModalBody.svelte';
    import ModalFooter from 'sveltestrap/src/ModalFooter.svelte';

    import {browser} from '$app/environment';
    import {goto} from '$app/navigation';
    import {post_call, patch_call, delete_call, check_accordion_active, nested_categories, process_nested_categories, uncheck_categories} from '$lib/utils.js';
    import {query, categories_all, categories, edit_dossier_categories, select_all, dossiers, start_date, end_date, current_dossier_id, add_dossier_open, dossier_accordion_active} from '$lib/store.js';

    import DossierNotif from './DossierNotif.svelte';
    import FilterParl from './FilterParl.svelte';
    import FilterEP from './FilterEP.svelte';
    import FilterPers from './FilterPers.svelte';

    import arrow_right from '../arrow-right.svg';
    import eigen_dossiers from '../eigen-dossiers.svg';
    import plus_icon from './plus.svg';
    import chevron from './chevron.svg';
    import pencil from './pencil.svg';
    import trash_can from './trash-can.svg';

    // Initialize the 'add dossier' variables
    let title = '';
    let notification_interval = 'direct';
    let notification_item_category_filter = [];

    // As this Dossiers component is used twice, we only want the 'add dossier'
    // modal to render once. If we don't do this then opening the 'add dossier'
    // modal using the $add_dossier_open store variable will open it twice (we
    // need this store variable to open it from the search bar 'add dossier'
    // button and dossier accordion 'add dossier' button).
    export let allow_modals = false;
    let add_dossier_open_modal = false;
    $: {
        if (allow_modals) {
            if ($add_dossier_open) {
                add_dossier_open_modal = true;
            } else {
                add_dossier_open_modal = false;
            }
        }
    }

    function toggle_add_dossier() {
        uncheck_select_all();
        $add_dossier_open = !$add_dossier_open;
    };

    // Initialize the 'edit dossier' variables
    let dossier_id_to_edit = '';
    let edit_dossier_data = {
        notification_interval: '',
        notification_item_category_filter: [],
        owner: '',
        query_data: {
            query: ''
        },
        title: '',
    };
    // Editing a dossier requires its own categories object, initialize it with
    // all categories and set them to false
    $categories_all.forEach(category => {
        $edit_dossier_categories[category['key']] = false;
    });
    let edit_dossier_open = false;
    function toggle_edit_dossier() {
        uncheck_select_all();
        edit_dossier_open = !edit_dossier_open
    };

    // Initialize the 'delete dossier' variables
    let dossier_id_to_delete = '';
    let dossier_name_to_delete = '';
    let delete_dossier_open = false;
    const toggle_delete_dossier = () => (delete_dossier_open = !delete_dossier_open);

    let search_info_open = false;
    const toggle_search_info = () => (search_info_open = !search_info_open);

    // If the user checked a nested_category facet, then uncheck all its
    // children as 1848 only stores the top category for dossiers
    function uncheck_nested_categories(notification_item_category_filter) {
        nested_categories.forEach(nested_category => {
            if (notification_item_category_filter.includes(nested_category)) {
                notification_item_category_filter = notification_item_category_filter.filter(function(category) {
                    if (!category.startsWith(nested_category + '|')) {
                        return category;
                    }
                })
            }
        });

        return notification_item_category_filter;
    }

    async function add_dossier() {
        // Get all categories that are checked by the user
        notification_item_category_filter = Object.keys($categories).filter(function(category) {
            return $categories[category];
        })

        notification_item_category_filter = uncheck_nested_categories(notification_item_category_filter);

        // Add the dossier
        let add_dossier_request = {
            endpoint: '/dossiers/',
            body: {
                notification_interval: notification_interval,
                notification_item_category_filter: notification_item_category_filter,
                owner: JSON.parse(Cookies.get('authenticatedAccount')).account_url,
                query_data: {
                    query: $query
                },
                title: title,
            }
        }
        let response = await post_call(add_dossier_request);

        // Clear the form values
        title = '';
        notification_interval = 'direct';
        notification_item_category_filter = [];

        goto(`${location}?dossier=${response.id}`, {invalidateAll: true});
    }

    // Initialize the edit dossier modal with relevant dossier data
    function init_edit_dossier_data() {
        // Get the edit dossier info
        edit_dossier_data = $dossiers.filter((dossier) => {
            return dossier.id == dossier_id_to_edit;
        })[0]

        // Uncheck all categories
        uncheck_categories(edit_dossier_categories);

        // Check the categories of the selected dossier
        edit_dossier_data.notification_item_category_filter.forEach(category => {
            $edit_dossier_categories[category] = true;
        });

        process_nested_categories($edit_dossier_categories);
    }

    async function edit_dossier() {
        // Get all categories that are checked by the user
        edit_dossier_data.notification_item_category_filter = Object.keys($edit_dossier_categories).filter(function(category) {
            return $edit_dossier_categories[category];
        })

        edit_dossier_data.notification_item_category_filter = uncheck_nested_categories(edit_dossier_data.notification_item_category_filter);

        // Edit the dossier
        let edit_dossier_request = {
            endpoint: `/dossiers/${dossier_id_to_edit}/`,
            body: {
                notification_interval: edit_dossier_data.notification_interval,
                notification_item_category_filter: edit_dossier_data.notification_item_category_filter,
                owner: JSON.parse(Cookies.get('authenticatedAccount')).account_url,
                query_data: {
                    query: edit_dossier_data.query_data.query
                },
                title: edit_dossier_data.title,
            }
        }
        let response = await patch_call(edit_dossier_request);

        dossier_id_to_edit = '';
        clear_dates();
        // Always go to the current feed of the dossier after editing it
        goto(`${location}?dossier=${response.id}`, {invalidateAll: true});
    }

    async function delete_dossier() {
        // Delete the dossier
        await delete_call({endpoint: `/dossiers/${dossier_id_to_delete}/`})

        // Remove the deleted dossier from the currently shown dossiers list
        $dossiers = $dossiers.filter((dossier) => {
            return dossier.id != dossier_id_to_delete;
        })

        // If the dossier to delete is currently selected or if the 'all'
        // dossiers view is shown, goto/reload the 'all' dossiers view
        if (dossier_id_to_delete == $current_dossier_id || $current_dossier_id == 'all') {
            dossier_id_to_delete = '';
            dossier_name_to_delete = '';
            clear_dates();
            uncheck_categories(categories);
            goto(location, {invalidateAll: true});
        // Otherwise just keep showing the current dossier/view
        } else {
            dossier_id_to_delete = '';
            dossier_name_to_delete = '';
        }
    }

    // Logic to deal with (un)checking select_all in new/edit dossier modals
    function process_select_all(categories) {
        // If the user checked select_all then check all its children
        if ($select_all) {
            // We don't want to blindly set all categories to true, because
            // it contains a few more (top)categories that aren't shown to
            // the user and if we set them to true, the user cant set them
            // back to false via the normal filters (if the user cancels
            // the creation of this dossier); also don't select the select_all inputs
            document.querySelectorAll(".accordion-body .checkboxes input:not(#new_dossier_select_all):not(#edit_dossier_select_all)").forEach(cat => {
                categories[cat.id.replace(/:.+$/, '').replace(/_/g, ' ')] = true;
            });
        }

        // If the user unchecked select_all, then uncheck all its children
        if (!$select_all) {
            Object.keys(categories).forEach(category => {
                categories[category] = false;
            })
        }
    }

    // We need this wrapper as calling this code in the reactive declaration
    // below results in an infinite loop
    function process_select_all_wrapper() {
        if (event.target.id == 'new_dossier_select_all') {
            process_select_all($categories);
            // Make sure that the newly set values are actually updated
            $categories = $categories;
        } else if (event.target.id == 'edit_dossier_select_all') {
            process_select_all($edit_dossier_categories);
            // Make sure that the newly set values are actually updated
            $edit_dossier_categories = $edit_dossier_categories;
        }
    }

    // If select_all is checked and another checkbox is clicked, then
    // uncheck select_all
    function uncheck_select_all() {
        if (event?.target.id != 'new_dossier_select_all' && event?.target.id != 'edit_dossier_select_all' && $select_all) {
            $select_all = false;
        }
    }

    // Clear dates; E.g. if a dossier is clicked, clear the date filters
    // because the user should always see the same state when clicking a
    // dossier
    function clear_dates() {
        $start_date = null;
        $end_date = null;
    }

    // Run process_select_all_wrapper when a user (un)checks select_all
    $: {
        $select_all;
        if (browser && event?.srcElement.closest('#new_dossier_select_all, #edit_dossier_select_all')) {
            process_select_all_wrapper();
        }
    }

    // Run when a user (un)checks a categories facet (Note: this can't be
    // merged with the same piece of code in app/+page.svelte, because the
    // reactivity of $select_all somehow won't work)
    $: {
        $categories;
        if (browser && event?.srcElement.closest('.row.checkboxes')) {
            uncheck_select_all();
            process_nested_categories($categories);
        }
    }

    // Run process_nested_categories when a user (un)checks an
    // edit_dossier_categories facet
    $: {
        $edit_dossier_categories;
        if (browser && event?.srcElement.closest('.row.checkboxes')) {
            uncheck_select_all();
            process_nested_categories($edit_dossier_categories);
        }
    }

    // '/app' or '/app/agenda'
    export let location;
    export let display = 'd-block';
    export let show = 'show';

    // Needed to correctly initialize the chevron in the 'Jouw dossiers'
    // accordion button
    let collapsed = '';
    let expanded = 'true';
    if (!show) {
        collapsed = 'collapsed';
        expanded = 'false';
    }

    // Needed to correctly initialize the chevron in the 'Filteren op soort
    // document' accordion buttons
    let collapsed_filters = '';
    let expanded_filters = 'true';
    $: {
        if (!$dossier_accordion_active) {
            collapsed_filters = 'collapsed';
            expanded_filters = 'false';
        } else {
            collapsed_filters = '';
            expanded_filters = 'true';
        }
    }
</script>

<div class="dossiers {display}">
    <div class="accordion" id="accordion-dossiers">
        <div class="accordion-item">
            <h2 class="accordion-header" id="accordion-dossiers-heading-one-{display}">
                <button class="accordion-button {collapsed}" type="button" data-bs-toggle="collapse" data-bs-target="#accordion-dossiers-collapse-one-{display}" aria-expanded={expanded} aria-controls="accordion-dossiers-collapse-one-{display}">
                    Jouw dossiers
                </button>
            </h2>
            <div id="accordion-dossiers-collapse-one-{display}" class="accordion-collapse collapse {show}" aria-labelledby="accordion-dossiers-heading-one-{display}" data-bs-parent="#accordion-dossiers">
                <div class="accordion-body">
                    <nav>
                        <ul>
                            {#if Array.isArray($dossiers) && $dossiers.length}
                                <li id="dossier_all" class="dossier d-grid {$current_dossier_id === 'all' ? 'selected' : ''}">
                                    <a href="{location}" data-sveltekit-preload-data="off" on:click={() => {clear_dates(); uncheck_categories(categories);}}>
                                        Al jouw dossiers
                                    </a>
                                </li>

                                {#each $dossiers as dossier}
                                    <li id="dossier_{dossier.id}" class="dossier d-grid {$current_dossier_id == dossier.id ? 'selected' : ''}">
                                        <a href="{location}?dossier={dossier.id}" data-sveltekit-preload-data="off" on:click={() => clear_dates()}>
                                            <div class="row gx-0">
                                                <div class="col-8 col-xl-9 col-xxl-8">{dossier.title}</div>

                                                <div class="col-4 col-xl-3 col-xxl-4 float-end">
                                                    <div class="float-end">
                                                        <button class="me-3" type="button" on:click|preventDefault={(event) => {event.stopPropagation(); dossier_id_to_edit = dossier.id; init_edit_dossier_data(); check_accordion_active($edit_dossier_categories); toggle_edit_dossier();}}>
                                                            <img id="wijzig-dossier" class="img-fluid pencil" src={pencil} title="Wijzig dossier" alt="Wijzig dossier '{dossier.title}'" width="14.94" height="14.94">
                                                        </button>

                                                        <button type="button" on:click|preventDefault={(event) => {event.stopPropagation(); dossier_id_to_delete = dossier.id; dossier_name_to_delete = dossier.title; toggle_delete_dossier();}}>
                                                            <img class="img-fluid trash-can" src={trash_can} title="Verwijder dossier" alt="Verwijder dossier '{dossier.title}'" width="12" height="15">
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    </li>
                                {/each}
                            {:else}
                                <p>
                                    U heeft nog geen dossiers aangemaakt. Door dossiers aan te maken kunt u de door u gewenste onderwerpen volgen en genotificeerd worden wanneer er hierover iets wordt gepubliceerd. Gebruik het zoekveld hiernaast om te beginnen, of de knop hieronder om direct een dossier aan te maken.
                                </p>
                            {/if}
                        </ul>
                    </nav>

                    <button type="button" on:click|preventDefault={(event) => {event.stopPropagation(); check_accordion_active($categories); toggle_add_dossier();}} class="new-dossier-button btn-medium text-white">
                        Nieuw dossier<img class="plus-icon" style="margin-left: 40px; width: 16px; position: relative; top: -1px;" src="{plus_icon}" alt="" width="16" height="16">
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>

<Modal isOpen={add_dossier_open_modal} {toggle_add_dossier} size='xl'>
    <form id='add-dossier-form' on:submit|preventDefault={add_dossier}>
        <button type="button" class="btn-close float-end pt-4 pe-4" aria-label="Sluiten" on:click={toggle_add_dossier}></button>
        <div class="modal-top">
            <div class="row">
                <div class="col-10 col-sm-9 col-lg-9">
                    <div class="modal-title">
                        <h2 class="mb-2">Maak een nieuw dossier aan</h2>
                    </div>
                    <p class="mb-0">
                        In een dossier houden we het nieuws bij over door jou gekozen onderwerpen. Het instellen kost even werk maar levert de meest relevante resultaten op.
                    </p>
                </div>
                <div class="col-2 col-sm-3 offset-lg-1 col-lg-2">
                    <img id="dossier-icon" src={eigen_dossiers} alt=''>
                </div>
            </div>
        </div>
        <ModalBody>
            <h3>1. Geef het dossier een naam</h3>
            <p>Deze naam is zichtbaar in het overzicht en de notificatiemails.</p>
            <fieldset class="form-group">
                <input class="form-control form-control-lg" bind:value={title} aria-label="Dossiernaam" required>
            </fieldset>

            <p class="voorbeeld"><i>Voorbeeld: ‘Coronabeleid’</i></p>

            <hr>

            <h3>2. Stel je zoekopdracht samen</h3>
            <p>Zoek met combinaties zoals <b>corona</b> en <b>rutte</b>. <a role="button" href="{''}" on:click={toggle_search_info}>Lees hier over extra zoekopties</a>.</p>
            <fieldset class="form-group">
                <input class="form-control form-control-lg" bind:value={$query} placeholder="Kies één of meerdere woorden" required>
            </fieldset>

            <div class="accordion" id="accordion">
                <div class="accordion-item">
                    <h4 class="accordion-header" id="heading-one">
                        <button class="accordion-button {collapsed_filters}" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-one" aria-expanded={expanded_filters} aria-controls="collapse-one">
                            Filteren op soort document
                        </button>
                    </h4>
                    <div id="collapse-one" class="accordion-collapse collapse {$dossier_accordion_active}" aria-labelledby="heading-one" data-bs-parent="#accordion">
                        <div class="accordion-body">
                            <div class="row checkboxes">
                                <p class="mt-1 mb-0">Vink niks aan als je alle documentsoorten wilt meenemen (ook toekomstige).</p>
                                <div class="col-12">
                                    <ul>
                                        <li>
                                            <label>
                                                <input type="checkbox" id="new_dossier_select_all" bind:checked={$select_all}>
                                                Alles selecteren
                                            </label>
                                        </li>
                                    </ul>
                                </div>

                                <div class="col-lg-6">
                                    <h5>Parlementaire documenten</h5>
                                    <FilterParl mb="mb-lg-0" filter_location=":add_dossier"/>
                                </div>

                                <div class="col-lg-6">
                                    <h5>Europees Parlement</h5>
                                    <FilterEP filter_location=":add_dossier"/>

                                    <h5>Persberichten</h5>
                                    <FilterPers filter_location=":add_dossier"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <DossierNotif bind:value={notification_interval}/>
        </ModalBody>
        <ModalFooter class="form-actions">
            <button type="submit" class="btn-medium bg-gold" on:click={() => {if (document.getElementById('add-dossier-form').checkValidity()) {toggle_add_dossier()}}}>Bewaar dossier <img src="{arrow_right}" alt="" class="ms-2" style="height: 14px; position: relative; top: -1px;" width="16.7667" height="14"></button>
            <button type="button" class="btn-medium bg-dark-blue me-3 text-white" on:click={toggle_add_dossier}>Terug</button>
        </ModalFooter>
    </form>
</Modal>

<Modal isOpen={edit_dossier_open} {toggle_edit_dossier} size='xl'>
    <form id='edit-dossier-form' on:submit|preventDefault={edit_dossier}>
        <button type="button" class="btn-close float-end pt-4 pe-4" aria-label="Sluiten" on:click={toggle_edit_dossier}></button>
        <div class="modal-top">
            <div class="row">
                <div class="col-10 col-sm-9 col-lg-9">
                    <div class="modal-title">
                        <h2 class="mb-0">Wijzig dossier "{edit_dossier_data.title}"</h2>
                    </div>
                </div>
                <div class="col-2 col-sm-3 offset-lg-1 col-lg-2">
                    <img id="dossier-icon" src={eigen_dossiers} alt=''>
                </div>
            </div>
        </div>
        <ModalBody>
            <h3>1. Geef het dossier een naam</h3>
            <p>Deze naam is zichtbaar in het overzicht en de notificatiemails</p>
            <fieldset class="form-group">
                <input class="form-control form-control-lg" bind:value={edit_dossier_data.title} aria-label="Dossiernaam" required>
            </fieldset>

            <p class="voorbeeld"><i>Voorbeeld: ‘Coronabeleid’</i></p>

            <hr>

            <h3>2. Stel je zoekopdracht samen</h3>
            <p>Zoek met combinaties zoals <b>corona</b> en <b>rutte</b>. <a role="button" href="{''}" on:click={toggle_search_info}>Lees hier over extra zoekopties</a>.</p>
            <fieldset class="form-group">
                <input class="form-control form-control-lg" bind:value={edit_dossier_data.query_data.query} placeholder="Kies één of meerdere woorden" required>
            </fieldset>

            <div class="accordion" id="accordion">
                <div class="accordion-item">
                    <h4 class="accordion-header" id="heading-one">
                        <button class="accordion-button {collapsed_filters}" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-one" aria-expanded={expanded_filters} aria-controls="collapse-one">
                            Filteren op soort document
                        </button>
                    </h4>
                    <div id="collapse-one" class="accordion-collapse collapse {$dossier_accordion_active}" aria-labelledby="heading-one" data-bs-parent="#accordion">
                        <div class="accordion-body">
                            <div class="row checkboxes">
                                <p class="mt-1 mb-0">Vink niks aan als je alle documentsoorten wilt meenemen (ook toekomstige).</p>
                                <div class="col-12">
                                    <ul>
                                        <li>
                                            <label>
                                                <input type="checkbox" id="edit_dossier_select_all" bind:checked={$select_all}>
                                                Alles selecteren
                                            </label>
                                        </li>
                                    </ul>
                                </div>

                                <div class="col-lg-6">
                                    <h5>Parlementaire documenten</h5>
                                    <FilterParl edit_dossier_context=true mb="mb-lg-0" filter_location=":edit_dossier"/>
                                </div>

                                <div class="col-lg-6">
                                    <h5>Europees Parlement</h5>
                                    <FilterEP edit_dossier_context=true filter_location=":edit_dossier"/>

                                    <h5>Persberichten</h5>
                                    <FilterPers edit_dossier_context=true filter_location=":edit_dossier"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <DossierNotif bind:value={edit_dossier_data.notification_interval}/>
        </ModalBody>
        <ModalFooter class="form-actions">
            <button type="submit" class="btn-medium bg-gold" on:click={() => {if (document.getElementById('edit-dossier-form').checkValidity()) {toggle_edit_dossier()}}}>Bewaar dossier <img src="{arrow_right}" alt="" class="ms-2" style="height: 14px; position: relative; top: -1px;" width="16.7667" height="14"></button>
            <button type="button" class="btn-medium bg-dark-blue me-3 text-white" on:click={toggle_edit_dossier}>Terug</button>
        </ModalFooter>
    </form>
</Modal>

<Modal isOpen={search_info_open} {toggle_search_info} size="lg">
    <div id="extra-zoekopties-top" class="modal-top">
        <button type="button" class="btn-close float-end pt-4 pe-4" aria-label="Sluiten" on:click={toggle_search_info}></button>
        <div class="modal-title">
            <h4>Extra zoekopties</h4>
        </div>
    </div>
    <ModalBody id="extra-zoekopties-body">
        <table class="table table-striped table-condensed table-responsive ng-scope">
            <thead>
                <tr>
                    <th>Zoekopdracht</th>
                    <th style="white-space: nowrap;">Vind documenten die…</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>vvd pvda</td>
                    <td>zowel de term "vvd" als "pvda" bevatten</td>
                </tr>
                <tr>
                    <td>vvd | pvda</td>
                    <td>minstens een van de termen "vvd" of "pvda" bevatten</td>
                </tr>
                <tr>
                    <td>vvd -pvda</td>
                    <td>de term "vvd" bevatten, maar <em>niet</em> "pvda"</td>
                </tr>
                <tr>
                    <td>"vvd pvda"</td>
                    <td>die de termen "vvd" en "pvda" in deze exacte volgorde bevatten</td>
                </tr>
                <tr>
                    <td>euro*</td>
                    <td>die termen bevatten die beginnen met "euro" (euro, Europa, Europees kampioenschap)</td>
                </tr>
                <tr>
                    <td>(vvd pvda) | euro*</td>
                    <td>die "vvd" <em>en</em> "pvda" bevatten, <em>of</em> termen die beginnen met "euro" (haakjes geven prioriteit aan)</td>
                </tr>
            </tbody>
        </table>
    </ModalBody>
</Modal>

<Modal isOpen={delete_dossier_open} {toggle_delete_dossier}>
    <div class="modal-top">
        <button id="remove-dossier-close" class="btn-close float-end pt-4 pe-4" aria-label="Sluiten" on:click={toggle_delete_dossier}></button>
        <div class="modal-title">
            <h2>Weet u zeker dat u dossier "<b>{dossier_name_to_delete}</b>" wilt verwijderen?</h2>
        </div>
    </div>
    <ModalFooter id="remove-dossier-footer">
        <button class="btn-medium bg-dark-blue text-white me-2 me-sm-3" on:click={toggle_delete_dossier}>Terug</button>
        <button class="btn-medium bg-red text-white" on:click={() => {toggle_delete_dossier(); delete_dossier();}}>
            Verwijder dossier &nbsp;<img style="height: 14px; filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(56deg) brightness(104%) contrast(104%);" class="img-fluid trash-can text-white" src={trash_can} alt="" role="presentation" width="11.2" height="14">
        </button>
    </ModalFooter>
</Modal>

<style lang="scss">
    .dossiers {
        border: 1px solid var(--light-blue-color4);
        margin-bottom: 30px;

        nav {
            margin: 0;
            padding: 8px 16px 13px;
        }

        ul {
            margin: 0;
            padding: 0;
            line-height: 20.8px;
            list-style-type: none;

            li {
                padding-top: 8px;
                padding-bottom: 8px;

                &:not(:last-child) {
                    border-bottom: 1px solid var(--light-blue-color4);
                }

                >a {
                    background-color: inherit;
                    color: var(--dark-blue-color) !important;
                    padding: 4px 12px;
                    border: 1px solid var(--light-blue-color);
                    text-decoration: none;

                    &:hover {
                        font-weight: 600;
                    }

                    div>div>div {
                        line-height: 0;
                    }

                    button {
                        padding: 3px 0 0 0 ;
                        background-color: inherit;
                        border: none;
                    }
                }
            }
        }
    }

    :global(.accordion-item) {
        background-color: inherit !important;
        border: none !important;
    }

    :global(.accordion-header) {
        margin: 0 !important;

        :global(button) {
            font-size: 20px !important;
            line-height: 20.8px !important;
        }
    }

    :global(.accordion-body) {
        padding: 0 !important;

        hr {
           border-top: 1px solid var(--light-blue-color4) !important;
        }
    }

    :global(.accordion-button) {
        box-shadow: none !important;
    }

    :global(.dossiers .accordion-button) {
        color: black !important;
        line-height: 20.8px !important;
        border: none !important;
        border-radius: 0 !important;

        background-color: var(--light-blue-color4) !important;
        padding: 13px 30px 12px;
    }

    .new-dossier-button {
        background-color: var(--dark-blue-color);
        margin: 0 16px 23px 16px;
        padding: 9px 14px 9px 22px;
        box-shadow: none;
        border-radius: 100px;
        border-width: 0;
        line-height: 22px;
        font-weight: bold;
    }

    :global(.modal) {
        p {
            font-size: 17px !important;
            line-height: 27px !important;
        }

        fieldset input {
            padding-left: 30px;
            border: 1px solid var(--light-blue-color4);
            border-radius: 80px;
            height: 50px;
            font-size: 16px;
            line-height: 22px;

            &:placeholder-shown {
               font-style: italic;
            }
        }
    }

    :global(.modal-content) {
        border-radius: 0 !important;
    }

    .modal-top {
        padding: 50px 40px 32px 40px;
        background-color: var(--light-blue-color);

        h2 {
            font-size: 30px !important;
            line-height: 34px !important;
        }
    }

    :global(.modal-body) {
        padding: 30px 40px 44px 40px !important;

        h3 {
            font-size: 24px !important;
            line-height: 24px !important;
        }

        hr {
            margin-top: 25px;
            margin-bottom: 23px;
        }

        :global(.accordion) {
            margin-top: 21px;

            :global(button) {
                padding: 0;
                font-family: var(--font-default);
                font-size: 18px;
                line-height: 20.8px;
                font-weight: bold;
                text-decoration: underline;
                background-color: white;
                color: black;
            }

            :global(.accordion-button::before) {
                margin-right: 10px;
                content: "";
                background-image: url('/src/routes/app/chevron.svg');
                background-size: 7px 14px;
                width: 7px;
                height: 14px;
            }

            :global(.accordion-button:not(.collapsed)::before) {
                transform: rotate(90deg);
            }
        }
    }

    :global(.modal .accordion-button::after) {
        display: none;
    }

    p.voorbeeld {
        margin-top: 5.5px;
        margin-bottom: 0;
        color: var(--dark-blue-color);
        font-size: 14px !important;
        line-height: 21px !important;
    }

    :global(.modal-footer) {
        border: none !important;
        padding: 0 40px 50px 40px !important;


        h3 {
            font-size: 24px !important;
            line-height: 24px !important;
        }

        :global(.btn-medium) {
            margin: 0;
            padding: 13px 20px 11px;
            box-shadow: none;
        }
    }

    :global(#remove-dossier-footer) {
        padding-top: 30px !important;
        justify-content: start !important;
    }

    #dossier-icon {
        width: 100px;
    }

    #extra-zoekopties-top {
        padding-top: 30px;
        padding-bottom: 10px;

        button {
            position: relative;
            top: -31px;
            left: 40px;
        }
    }

    :global(#extra-zoekopties-body) table {
        word-break: normal !important;
    }

    #remove-dossier-close {
        position: relative;
        top: -50px;
        left: 40px;
    }

    :global(.form-actions) {
        display: flex;
        flex-direction: row-reverse; /* Reverse the elements inside */
    }

    .pencil:hover {
        filter: invert(69%) sepia(21%) saturate(795%) hue-rotate(1deg) brightness(96%) contrast(92%);
    }

    .trash-can:hover {
        filter: invert(18%) sepia(100%) saturate(5515%) hue-rotate(358deg) brightness(92%) contrast(121%);
    }


    @media (max-width: 575px) {
        #dossier-icon {
            width: 55px;
        }

        :global(.modal-footer) {
            :global(.btn-medium) {
                padding-left: 16px;
                padding-right: 16px;
            }
        }

        :global(#extra-zoekopties-body) {
            padding-left: 12px !important;
            padding-right: 12px !important;
        }
    }
</style>
